export const SMART_SEARCH_STEPS = [
  { id: 1, label: "Destination" },
  { id: 2, label: "Duration" },
  { id: 3, label: "Travel Type" },
  { id: 4, label: "Interest" },
];

export const DURATION_OPTIONS = [
  { id: "3-5", label: "3-5 Days", nights: [3, 4, 5] },
  { id: "6-8", label: "6-8 Days", nights: [6, 7, 8] },
  { id: "9-12", label: "9-12 Days", nights: [9, 10, 11, 12] },
  { id: "13-15", label: "13-15 Days", nights: [13, 14, 15] },
  { id: "15+", label: "15+ Days", nights: [16, 17, 18, 19, 20] },
  { id: "custom", label: "Custom Duration", nights: [] },
];

export const TRAVEL_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function extractArray(root) {
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.Data)) return root.Data;
  if (Array.isArray(root?.holidayList)) return root.holidayList;
  if (Array.isArray(root?.list)) return root.list;
  if (Array.isArray(root?.items)) return root.items;
  if (Array.isArray(root?.result)) return root.result;

  const nested = root?.data ?? root?.Data;
  if (nested && typeof nested === "object") {
    return extractArray(nested);
  }

  return [];
}

export function sanitizeSmartSearchImageUrl(url) {
  if (!url || typeof url !== "string") return "";
  let cleaned = url.trim();
  const host = "https://blueberry.azureedge.net/";
  while (cleaned.startsWith(`${host}${host}`)) {
    cleaned = cleaned.slice(host.length);
  }
  return cleaned;
}

function pickDestinationImage(item = {}) {
  return sanitizeSmartSearchImageUrl(
    item.imageURL ||
      item.imageUrl ||
      item.imagePath ||
      item.webImagePath ||
      item.mobImagePath ||
      item.image ||
      ""
  );
}

function extractSmartSearchDestinationItems(response) {
  const payload = response?.data ?? response?.Data ?? response;
  if (!payload || typeof payload !== "object") {
    return extractArray(response).map((item) => ({ ...item, __kind: "legacy" }));
  }

  const cities = payload.cities ?? payload.Cities;
  const countries = payload.countries ?? payload.Countries;
  const states = payload.states ?? payload.States;
  const regions = payload.regions ?? payload.Regions;

  const hasBuckets =
    Array.isArray(cities) ||
    Array.isArray(countries) ||
    Array.isArray(states) ||
    Array.isArray(regions);

  if (!hasBuckets) {
    return extractArray(response).map((item) => ({ ...item, __kind: "legacy" }));
  }

  const items = [];
  if (Array.isArray(cities)) {
    cities.forEach((row) => items.push({ ...row, __kind: "city" }));
  }
  if (Array.isArray(countries)) {
    countries.forEach((row) => items.push({ ...row, __kind: "country" }));
  }
  if (Array.isArray(states)) {
    states.forEach((row) => items.push({ ...row, __kind: "state" }));
  }
  if (Array.isArray(regions)) {
    regions.forEach((row) => items.push({ ...row, __kind: "region" }));
  }
  return items;
}

function mapSmartSearchDestinationItem(item, index) {
  const kind = item.__kind || "legacy";

  if (kind === "city") {
    const cityId = Number(item.cityId) || 0;
    const label = item.cityName || item.name || item.label || "";
    return {
      id: cityId ? `city-${cityId}` : `city-${index}`,
      label,
      image: pickDestinationImage(item),
      continentId: Number(item.continentId) || 0,
      regionId: Number(item.regionId) || 0,
      countryId: Number(item.countryId) || 0,
      stateId: Number(item.stateId) || 0,
      cityId,
    };
  }

  if (kind === "country") {
    const countryId = Number(item.id ?? item.countryId) || 0;
    const label = item.name || item.countryName || item.label || "";
    return {
      id: countryId ? `country-${countryId}` : `country-${index}`,
      label,
      image: pickDestinationImage(item),
      continentId: Number(item.continentId) || 0,
      regionId: Number(item.regionId) || 0,
      countryId,
      stateId: 0,
      cityId: 0,
    };
  }

  if (kind === "state") {
    const stateId = Number(item.id ?? item.stateId) || 0;
    const label = item.name || item.stateName || item.label || "";
    return {
      id: stateId ? `state-${stateId}` : `state-${index}`,
      label,
      image: pickDestinationImage(item),
      continentId: Number(item.continentId) || 0,
      regionId: Number(item.regionId) || 0,
      countryId: Number(item.countryId) || 0,
      stateId,
      cityId: 0,
    };
  }

  if (kind === "region") {
    const regionId = Number(item.id ?? item.regionId) || 0;
    const label = item.name || item.regionName || item.label || "";
    return {
      id: regionId ? `region-${regionId}` : `region-${index}`,
      label,
      image: pickDestinationImage(item),
      continentId: Number(item.continentId) || 0,
      regionId,
      countryId: 0,
      stateId: 0,
      cityId: 0,
    };
  }

  return {
    id:
      item.cityId ||
      item.countryId ||
      item.destinationId ||
      item.id ||
      index + 1,
    label:
      item.cityName ||
      item.countryName ||
      item.destinationName ||
      item.name ||
      item.label ||
      "",
    image: pickDestinationImage(item),
    continentId: Number(item.continentId) || 0,
    regionId: Number(item.regionId) || 0,
    countryId: Number(item.countryId) || 0,
    stateId: Number(item.stateId) || 0,
    cityId: Number(item.cityId) || 0,
  };
}

function extractSmartSearchReviewItems(response) {
  const payload = response?.data ?? response?.Data ?? response;
  if (payload && typeof payload === "object") {
    const lists = [
      payload.reviews,
      payload.Reviews,
      payload.reviewList,
      payload.ReviewList,
      payload.randomReviews,
      payload.RandomReviews,
      payload.items,
      payload.list,
    ];
    for (const list of lists) {
      if (Array.isArray(list) && list.length) return list;
    }
  }
  return extractArray(response);
}

export function normalizeSmartSearchList(response) {
  return extractArray(response);
}

export function normalizeSmartSearchOptions(response) {
  return extractArray(response).map((item, index) => ({
    id: item.id ?? item.travelTypeId ?? item.interestTypeId ?? item.experienceTypeId ?? index + 1,
    label:
      item.name ||
      item.label ||
      item.travelTypeName ||
      item.interestTypeName ||
      item.experienceTypeName ||
      item.title ||
      "",
    image:
      item.webImage ||
      item.imageUrl ||
      item.webImagePath ||
      item.mobImagePath ||
      item.image ||
      "",
  }));
}

export function normalizeSmartSearchDestinations(response) {
  return extractSmartSearchDestinationItems(response)
    .map(mapSmartSearchDestinationItem)
    .filter((item) => item.label);
}

export function normalizeSmartSearchReviews(response) {
  return extractSmartSearchReviewItems(response)
    .map((item, index) => ({
      id: item.reviewId || item.id || index + 1,
      userName:
        item.userName ||
        item.customerName ||
        item.name ||
        item.fullName ||
        "Traveler",
      userImageUrl: sanitizeSmartSearchImageUrl(
        item.userImageUrl ||
          item.userImage ||
          item.imageUrl ||
          item.imageURL ||
          item.profileImage ||
          ""
      ),
      reviewText:
        item.reviewText ||
        item.reviewDescription ||
        item.description ||
        item.review ||
        item.comment ||
        "",
      rating: Number(item.rating ?? item.starRating ?? item.stars) || 5,
      tripToCity:
        item.tripToCity ||
        item.tripTo ||
        item.destinationName ||
        item.packageName ||
        item.trip ||
        item.cityName ||
        "",
    }))
    .filter((item) => item.reviewText || item.userName);
}

export function buildSmartSearchPayload(selection) {
  const { destination, duration, travelType, experienceType } = selection;

  return {
    continentId: destination?.continentId || 0,
    regionId: destination?.regionId || 0,
    countryId: destination?.countryId || 0,
    stateId: destination?.stateId || 0,
    cityId: destination?.cityId || 0,
    nights: duration?.nights?.length ? duration.nights : [],
    travelType: Number(travelType?.id) || 0,
    interestType: Number(experienceType?.id) || 0,
    experienceType: Number(experienceType?.id) || 0,
    pageSize: 12,
    pageIndex: 1,
    sortBy: "",
  };
}

export function buildSmartSearchEnquiryPayload(selection, form, currentPageUrl = "") {
  const { destination, duration, travelType, experienceType } = selection;

  return {
    continentId: destination?.continentId || 0,
    regionId: destination?.regionId || 0,
    countryId: destination?.countryId || 0,
    stateId: destination?.stateId || 0,
    cityId: destination?.cityId || 0,
    travelTypeId: Number(travelType?.id) || 0,
    interestTypeId: Number(experienceType?.id) || 0,
    experienceTypeId: Number(experienceType?.id) || 0,
    nights: duration?.label || "",
    fullName: form.fullName?.trim() || "",
    countryCode: form.countryCode || "",
    mobile: form.mobile || "",
    email: form.email?.trim() || "",
    destination: form.destination?.trim() || destination?.label || "",
    travelMonth: form.travelMonth || "",
    currentPageUrl,
  };
}

export function isSmartSearchSuccess(response) {
  const status = String(response?.status || "").toLowerCase();
  return !status || status === "success";
}
